/**
 * Settings screen wiring: volumes, reduced motion, quality tier, latency
 * display, calibration entry, reset save (double-press confirm — destructive
 * actions get one guard, never a modal). Persists through SaveStore.
 */
import type { SaveStore, SaveV1 } from "../data/saveStore";
import type { AudioSystem } from "../audio/audioSystem";
import type { QualityTier } from "../core/quality";

function el<T extends HTMLElement>(id: string): T {
  const node = document.getElementById(id);
  if (!node) throw new Error(`missing #${id}`);
  return node as T;
}

export class SettingsUI {
  private musicVol = el<HTMLInputElement>("set-music-vol");
  private sfxVol = el<HTMLInputElement>("set-sfx-vol");
  private reducedMotion = el<HTMLInputElement>("set-reduced-motion");
  private quality = el<HTMLSelectElement>("set-quality");
  private qualityDetected = el<HTMLElement>("set-quality-detected");
  private latencyValue = el<HTMLElement>("set-latency-value");
  private resetBtn = el<HTMLButtonElement>("btn-reset-save");
  private resetArmed = false;
  /** What "auto" resolved to this session (shown as a hint). */
  detectedTier: QualityTier | null = null;

  constructor(
    private readonly save: SaveStore,
    private readonly audio: AudioSystem,
    hooks: {
      onBack: () => void;
      onCalibrate: () => void;
      onReducedMotion: (on: boolean) => void;
      onQuality: (setting: SaveV1["settings"]["quality"]) => void;
      onResetSave: () => void;
    },
  ) {
    el("btn-settings-back").addEventListener("click", hooks.onBack);
    el("btn-calibrate").addEventListener("click", hooks.onCalibrate);

    this.musicVol.addEventListener("input", () => {
      const v = Number(this.musicVol.value) / 100;
      this.save.updateSettings({ musicVol: v });
      this.audio.setMusicVolume(v);
    });
    this.sfxVol.addEventListener("input", () => {
      const v = Number(this.sfxVol.value) / 100;
      this.save.updateSettings({ sfxVol: v });
      this.audio.setSfxVolume(v);
    });
    this.reducedMotion.addEventListener("change", () => {
      this.save.updateSettings({ reducedMotion: this.reducedMotion.checked });
      hooks.onReducedMotion(this.reducedMotion.checked);
    });
    this.quality.addEventListener("change", () => {
      const v = this.quality.value as SaveV1["settings"]["quality"];
      this.save.updateSettings({ quality: v });
      hooks.onQuality(v);
      this.refresh();
    });
    this.resetBtn.addEventListener("click", () => {
      if (!this.resetArmed) {
        this.resetArmed = true;
        this.resetBtn.textContent = "Tap again to erase everything";
        setTimeout(() => {
          this.resetArmed = false;
          this.resetBtn.textContent = "Reset save";
        }, 2500);
        return;
      }
      hooks.onResetSave();
    });
  }

  /** Sync widgets from the save before showing the screen. */
  refresh(): void {
    const s = this.save.settings;
    this.musicVol.value = String(Math.round(s.musicVol * 100));
    this.sfxVol.value = String(Math.round(s.sfxVol * 100));
    this.reducedMotion.checked = s.reducedMotion;
    this.quality.value = s.quality;
    this.qualityDetected.textContent =
      s.quality === "auto" && this.detectedTier ? `(auto → ${this.detectedTier})` : "";
    this.latencyValue.textContent = `${s.latencyOffsetMs} ms`;
  }
}
